import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa6";

import Badge from "@/components/Badges";
import Loader from "@/components/Loader.jsx";
import ModalBasic from "@/components/ModalBasic.jsx";
import PaginationNumeric from "@/components/PaginationNumeric.jsx";
import { PrimaryButton, SecondaryButton } from "@/components/Buttons.jsx";
import { getIngestionHistoryItems } from "@/api/externalFeeds.js";

const DEFAULT_LIMIT = 50;
const SELECT_ALL = "all";

const noop = () => {};

function isSelectAllMode(selection) {
  return selection.length === 1 && selection[0] === SELECT_ALL;
}

function unmatchedRowKey(row) {
  return String(row?.pos_upc || "").trim();
}

function FixedDropdown({
  show,
  onClose,
  triggerRef,
  menuRef,
  estimatedHeight,
  align,
  children,
}) {
  const [pos, setPos] = useState({ top: 0, left: 0, right: undefined });

  useEffect(() => {
    if (!show || !triggerRef?.current) return;
    const btn = triggerRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const menuWidth = 224;
    const h = estimatedHeight;

    const wouldOverflowBottom = btn.bottom + h > vh;
    const top = wouldOverflowBottom ? btn.top - h : btn.bottom;

    if (align === "right") {
      setPos({
        top,
        right: vw - btn.right,
        left: undefined,
      });
    } else {
      let left = btn.left;
      if (left + menuWidth > vw - 8) left = Math.max(8, vw - menuWidth - 8);
      if (left < 8) left = 8;
      setPos({ top, left, right: undefined });
    }
  }, [show, triggerRef, estimatedHeight, align]);

  useEffect(() => {
    const handleOutside = (e) => {
      const inMenu = menuRef.current?.contains(e.target);
      const inTrigger = triggerRef.current?.contains(e.target);
      if (!inMenu && !inTrigger) onClose();
    };
    if (show) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [show, onClose, menuRef, triggerRef]);

  if (!show) return null;

  return (
    <div
      ref={menuRef}
      className="fixed w-56 py-2 z-50 bg-white dark:bg-theme-surface-accent rounded-sm shadow-lg border border-slate-200 dark:border-slate-700"
      style={{
        top: `${pos.top}px`,
        ...(pos.right != null ? { right: `${pos.right}px` } : { left: `${pos.left}px` }),
      }}
    >
      {children}
    </div>
  );
}

const TABS = [
  { id: "all", label: "All Items" },
  { id: "matched", label: "Matched" },
  { id: "unmatched", label: "Unmatched" },
];

function formatTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function formatPrice(cents) {
  if (cents == null) return "—";
  const value = Number(cents);
  if (!Number.isFinite(value) || value === 0) return "—";
  return `$${(value / 100).toFixed(2)}`;
}

function formatQuantity(qty) {
  if (qty == null) return "—";
  const value = Number(qty);
  if (!Number.isFinite(value)) return "—";
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toFixed(2);
}

function matchStatusBadge(status) {
  switch (status) {
    case "auto":
      return <Badge color="blue">Auto</Badge>;
    case "manual":
      return <Badge color="purple">Manual</Badge>;
    case "not_matched":
      return <Badge color="gray">Not matched</Badge>;
    default:
      return status ? <Badge color="gray">{status}</Badge> : null;
  }
}

function unmatchedStatusBadge(status) {
  switch (status) {
    case "processed":
      return <Badge color="green">Processed</Badge>;
    case "processing":
      return <Badge color="yellow">Processing</Badge>;
    case "failed":
      return <Badge color="red">Failed</Badge>;
    case "new":
      return <Badge color="yellow">Unmatched</Badge>;
    default:
      return <Badge color="yellow">Unmatched</Badge>;
  }
}

function ProductCell({ product, draftLabel }) {
  if (!product) return null;
  return (
    <div className="flex items-start gap-3">
      {product.image ? (
        <img
          src={product.image}
          alt=""
          className="h-10 w-10 rounded object-cover bg-slate-100 dark:bg-slate-800 shrink-0"
        />
      ) : (
        <div
          className="h-10 w-10 rounded bg-slate-100 dark:bg-slate-800 shrink-0"
          aria-hidden
        />
      )}
      <div className="min-w-0">
        {draftLabel && (
          <div className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 dark:text-indigo-400 mb-0.5">
            {draftLabel}
          </div>
        )}
        <div className="font-medium text-slate-800 dark:text-slate-100 break-words">
          {productLabel(product)}
        </div>
        {product.company && (
          <div className="text-[11px] text-slate-500 dark:text-slate-400">
            {product.company}
          </div>
        )}
      </div>
    </div>
  );
}

function productLabel(p) {
  if (!p) return "—";
  const title = String(p.title || "").trim();
  const subtitle = String(p.subtitle || "").trim();
  if (title && subtitle) return `${title} — ${subtitle}`;
  return title || subtitle || p.sku_id || "—";
}

const tabBtnClass = (active) =>
  `inline-flex items-center justify-center text-xs font-medium leading-5 rounded-full px-3 py-1 border shadow-sm duration-150 ease-in-out ${
    active
      ? "border-theme-button-primary bg-theme-button-primary text-theme-button-primary-text"
      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
  }`;

function CountPill({ active, label, count }) {
  return (
    <span
      className={`ml-2 inline-flex items-center justify-center min-w-[1.5rem] px-1.5 text-[10px] font-semibold rounded-full ${
        active
          ? "bg-white/30 text-white"
          : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200"
      }`}
      aria-label={`${label} count: ${count}`}
    >
      {Number(count).toLocaleString()}
    </span>
  );
}

const IngestionDetail = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);

  const [selection, setSelection] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectAllModalOpen, setSelectAllModalOpen] = useState(false);
  const bulkBtnRef = useRef(null);
  const bulkMenuRef = useRef(null);
  const headerCheckboxRef = useRef(null);

  const isUnmatchedTab = tab === "unmatched";

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQ]);

  useEffect(() => {
    setSelection([]);
    setBulkOpen(false);
  }, [tab]);

  useEffect(() => {
    setSelection([]);
    setBulkOpen(false);
  }, [page, debouncedQ]);

  const fetchItems = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getIngestionHistoryItems(jobId, {
        page,
        limit: DEFAULT_LIMIT,
        q: debouncedQ || undefined,
      });
      setPayload(res?.data ?? null);
    } catch (e) {
      console.error("Failed to load ingestion items:", e);
      setError(
        e?.response?.data ||
          e?.message ||
          "Failed to load items for this ingestion run"
      );
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [jobId, page, debouncedQ]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const allItems = payload?.items ?? [];
  const total = payload?.total ?? 0;
  const totalPages = Math.max(1, payload?.total_pages ?? 1);

  // The endpoint no longer accepts a matched/unmatched filter, and its own
  // `counts` are page-scoped anyway (`counts_scope: "page"`), so the tabs
  // filter the page in memory rather than costing a request each.
  const items = useMemo(
    () => (tab === "all" ? allItems : allItems.filter((row) => row.type === tab)),
    [allItems, tab]
  );

  const counts = useMemo(() => {
    const matched = allItems.filter((row) => row.type === "matched").length;
    const unmatched = allItems.filter((row) => row.type === "unmatched").length;
    return { matched, unmatched, total: allItems.length };
  }, [allItems]);

  const summary = useMemo(() => {
    if (total === 0) return "No items";
    const start = (page - 1) * DEFAULT_LIMIT + 1;
    const end = Math.min(page * DEFAULT_LIMIT, Number(total));
    const range = `${start.toLocaleString()}–${end.toLocaleString()} of ${Number(total).toLocaleString()}`;
    if (tab === "all") return range;
    return `${items.length.toLocaleString()} ${tab} on this page · ${range}`;
  }, [total, page, tab, items.length]);

  const tabCount = (id) => {
    if (id === "matched") return counts.matched ?? 0;
    if (id === "unmatched") return counts.unmatched ?? 0;
    return counts.total ?? 0;
  };

  const visibleUnmatchedKeys = useMemo(
    () =>
      items
        .filter((row) => row.type === "unmatched")
        .map(unmatchedRowKey)
        .filter(Boolean),
    [items]
  );

  const allVisibleSelected =
    visibleUnmatchedKeys.length > 0 &&
    (isSelectAllMode(selection) ||
      visibleUnmatchedKeys.every((key) => selection.includes(key)));

  const someVisibleSelected =
    !isSelectAllMode(selection) &&
    visibleUnmatchedKeys.some((key) => selection.includes(key)) &&
    !allVisibleSelected;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someVisibleSelected;
    }
  }, [someVisibleSelected]);

  const selectedCount = useMemo(() => {
    if (!isUnmatchedTab) return 0;
    // null means "every unmatched row in the run" — a figure this endpoint can
    // no longer give us, since it dropped the matched/unmatched filter and its
    // own counts cover only the page.
    if (isSelectAllMode(selection)) return null;
    return selection.length;
  }, [isUnmatchedTab, selection]);

  useEffect(() => {
    if (selectedCount === 0) setBulkOpen(false);
  }, [selectedCount]);

  const isRowSelected = useCallback(
    (key) => {
      if (!key) return false;
      if (isSelectAllMode(selection)) return true;
      return selection.includes(key);
    },
    [selection]
  );

  const toggleRow = useCallback(
    (key) => {
      if (!key) return;
      if (isSelectAllMode(selection)) {
        setSelection(visibleUnmatchedKeys.filter((k) => k !== key));
        return;
      }
      setSelection((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    },
    [selection, visibleUnmatchedKeys]
  );

  const clearSelection = useCallback(() => {
    setSelection([]);
    setBulkOpen(false);
  }, []);

  const selectThisPageOnly = useCallback(() => {
    setSelection(visibleUnmatchedKeys);
    setSelectAllModalOpen(false);
  }, [visibleUnmatchedKeys]);

  const selectAllPages = useCallback(() => {
    setSelection([SELECT_ALL]);
    setSelectAllModalOpen(false);
  }, []);

  const handleHeaderCheckboxChange = useCallback(() => {
    if (isSelectAllMode(selection) || allVisibleSelected) {
      clearSelection();
      return;
    }
    if (totalPages > 1) {
      setSelectAllModalOpen(true);
      return;
    }
    setSelection(visibleUnmatchedKeys);
  }, [
    selection,
    allVisibleSelected,
    totalPages,
    visibleUnmatchedKeys,
    clearSelection,
  ]);

  const handleTabChange = useCallback((nextTab) => {
    setSelection([]);
    setBulkOpen(false);
    setTab(nextTab);
  }, []);

  const tableColSpan = isUnmatchedTab ? 8 : 7;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 w-full max-w-9xl mx-auto">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <Link
            to="/external-feeds/ingestion-history"
            className="inline-flex items-center text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <FaArrowLeft className="w-3 h-3 mr-1.5" />
            Back to ingestion history
          </Link>
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold truncate">
            Ingestion run details
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-mono break-all">
            Job ID: {jobId}
          </p>
        </div>
        <SecondaryButton onClick={() => navigate(-1)}>Close</SecondaryButton>
      </div>

      <div className="bg-white dark:bg-theme-surface-accent shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
        <header className="px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-col gap-2 min-w-0">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                View
              </span>
              <div className="flex flex-wrap gap-2">
                {TABS.map((t) => {
                  const active = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={tabBtnClass(active)}
                      onClick={() => handleTabChange(t.id)}
                    >
                      <span>{t.label}</span>
                      <CountPill
                        active={active}
                        label={t.label}
                        count={tabCount(t.id)}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-end shrink-0 w-full lg:w-auto">
              {isUnmatchedTab && (
                <div className="flex flex-col items-start gap-1 shrink-0">
                  <div className="relative">
                    <button
                      type="button"
                      ref={bulkBtnRef}
                      disabled={selectedCount === 0}
                      onClick={() => setBulkOpen((o) => !o)}
                      className={`inline-flex items-center gap-2 rounded border px-3 py-2 text-sm font-medium shadow-sm ${
                        selectedCount === 0
                          ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-600"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      }`}
                    >
                      Bulk actions
                      <svg
                        className="w-4 h-4 opacity-70"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                      </svg>
                    </button>
                    <FixedDropdown
                      show={bulkOpen && selectedCount > 0}
                      onClose={() => setBulkOpen(false)}
                      triggerRef={bulkBtnRef}
                      menuRef={bulkMenuRef}
                      estimatedHeight={96}
                      align="left"
                    >
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
                        onClick={() => {
                          setBulkOpen(false);
                          noop();
                        }}
                      >
                        Create products
                      </button>
                      <button
                        type="button"
                        className="w-full text-left px-4 py-2 text-sm text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-900"
                        onClick={() => {
                          setBulkOpen(false);
                          noop();
                        }}
                      >
                        Ignore Products
                      </button>
                    </FixedDropdown>
                  </div>
                  {(selectedCount === null || selectedCount > 0) && (
                    <span className="text-xs text-slate-600 dark:text-slate-400">
                      {selectedCount === null
                        ? "All unmatched selected (all pages)"
                        : `${Number(selectedCount).toLocaleString()} selected`}
                    </span>
                  )}
                </div>
              )}
              <div className="w-full sm:max-w-sm">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  Search
                </label>
                <input
                  type="search"
                  className="form-input w-full"
                  placeholder="POS name, UPC, SKU, Saucey SKU"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400 sm:pb-2">
                {summary}
              </div>
            </div>
          </div>
        </header>

        {error && (
          <div className="px-5 py-3 border-b border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 text-sm text-rose-700 dark:text-rose-200">
            {typeof error === "string" ? error : "Failed to load items for this ingestion run"}
          </div>
        )}

        <div className="relative min-h-[300px]">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 dark:bg-slate-900/50">
              <Loader />
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="table-auto w-full text-sm dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
              <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20">
                <tr>
                  {isUnmatchedTab && (
                    <th className="pl-5 pr-2 py-3 w-px">
                      <input
                        ref={headerCheckboxRef}
                        type="checkbox"
                        className="form-checkbox h-4 w-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600"
                        checked={
                          isSelectAllMode(selection) ||
                          (allVisibleSelected && visibleUnmatchedKeys.length > 0)
                        }
                        onChange={handleHeaderCheckboxChange}
                        disabled={visibleUnmatchedKeys.length === 0 || loading}
                        aria-label="Select all on this page"
                      />
                    </th>
                  )}
                  <th className="px-2 first:pl-5 last:pr-5 py-3 text-left font-semibold whitespace-nowrap">
                    Status
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 text-left font-semibold min-w-[16rem]">
                    POS Item
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 text-left font-semibold whitespace-nowrap">
                    <span className="block">POS UPC</span>
                    <span className="block">Retailer SKU</span>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 text-right font-semibold whitespace-nowrap">
                    POS Price
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 text-right font-semibold whitespace-nowrap">
                    Qty
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 text-left font-semibold min-w-[16rem]">
                    Saucey Product
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 text-left font-semibold whitespace-nowrap">
                    Saucey SKU
                  </th>
                </tr>
              </thead>
              <tbody>
                {!loading && items.length === 0 && (
                  <tr>
                    <td
                      colSpan={tableColSpan}
                      className="px-5 py-12 text-center text-slate-500 dark:text-slate-400"
                    >
                      {allItems.length > 0 ? (
                        <>No {tab} items on this page. Other pages of this run
                        may still have some.</>
                      ) : (
                        <>No items recorded for this ingestion run yet. If the
                        job ran before per-item tracking was enabled, items will
                        not appear here.</>
                      )}
                    </td>
                  </tr>
                )}
                {items.map((row, idx) => {
                  const rowKey = unmatchedRowKey(row);
                  const showCheckbox = isUnmatchedTab && row.type === "unmatched";
                  return (
                  <tr
                    key={`${row.type}-${row.pos_id || row.pos_upc || idx}`}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  >
                    {showCheckbox && (
                      <td className="pl-5 pr-2 py-3 align-top">
                        <input
                          type="checkbox"
                          className="form-checkbox h-4 w-4 text-indigo-600 rounded border-slate-300 dark:border-slate-600"
                          checked={isRowSelected(rowKey)}
                          onChange={() => toggleRow(rowKey)}
                          disabled={!rowKey}
                          aria-label={rowKey ? `Select ${rowKey}` : "Select row"}
                        />
                      </td>
                    )}
                    <td className="px-2 first:pl-5 last:pr-5 py-3 align-top">
                      {row.type === "matched" ? (
                        matchStatusBadge(row.match_status)
                      ) : (
                        <div className="flex flex-col gap-1 items-start">
                          {unmatchedStatusBadge(row.unmatched_status)}
                          {row.unmatched_status === "failed" &&
                            row.last_autocreate_error && (
                              <span
                                className="text-[10px] text-rose-600 dark:text-rose-400 max-w-[12rem] line-clamp-2"
                                title={row.last_autocreate_error}
                              >
                                {row.last_autocreate_error}
                              </span>
                            )}
                        </div>
                      )}
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 align-top">
                      <div className="font-medium text-slate-800 dark:text-slate-100 break-words">
                        {row.pos_name || "—"}
                      </div>
                      {row.pos_id && (
                        <div className="text-[11px] text-slate-400 dark:text-slate-500 font-mono break-all">
                          {row.pos_id}
                        </div>
                      )}
                      {row.type === "unmatched" && row.store_count > 1 && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                          Seen in {row.store_count} stores
                        </div>
                      )}
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 align-top font-mono text-slate-700 dark:text-slate-200">
                      <div>{row.pos_upc || "—"}</div>
                      <div>{row.retailer_sku || "—"}</div>
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 align-top text-right tabular-nums text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      {formatPrice(row.pos_price_cents)}
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 align-top text-right tabular-nums text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      {formatQuantity(row.qty)}
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 align-top">
                      {row.type === "matched" && row.saucey_product ? (
                        <ProductCell product={row.saucey_product} />
                      ) : row.type === "unmatched" && row.draft_product ? (
                        <ProductCell product={row.draft_product} draftLabel="Draft product" />
                      ) : row.type === "unmatched" &&
                        row.unmatched_status === "processed" ? (
                        <span className="text-slate-500 dark:text-slate-400 italic text-sm">
                          Draft product created (reload if details are missing)
                        </span>
                      ) : (
                        <span className="text-slate-400 dark:text-slate-500 italic">
                          {row.type === "unmatched"
                            ? row.last_observed
                              ? `Last observed ${formatTimestamp(row.last_observed)}`
                              : "Awaiting product match"
                            : "—"}
                        </span>
                      )}
                    </td>
                    <td className="px-2 first:pl-5 last:pr-5 py-3 align-top font-mono text-xs text-slate-700 dark:text-slate-200 whitespace-nowrap">
                      {row.saucey_sku_id ||
                        row.draft_product?.sku_id ||
                        "—"}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {totalPages > 1 && (
          <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-700">
            <PaginationNumeric
              currentPage={page - 1}
              onSelect={(zeroBased) => setPage(zeroBased + 1)}
              totalPages={totalPages}
              isFirstPage={page <= 1}
              isLastPage={page >= totalPages}
            />
          </div>
        )}
      </div>

      <ModalBasic
        id="ingestion-unmatched-select-all"
        title="Select unmatched items"
        modalOpen={selectAllModalOpen}
        setModalOpen={setSelectAllModalOpen}
        width="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-400">
            This run has {Number(total).toLocaleString()} items across{" "}
            {totalPages} pages. Do you want to select only the{" "}
            {visibleUnmatchedKeys.length.toLocaleString()} unmatched on this
            page, or every unmatched item in the run?
          </p>
          <div className="flex flex-wrap gap-2 justify-end">
            <SecondaryButton
              type="button"
              onClick={() => setSelectAllModalOpen(false)}
            >
              Cancel
            </SecondaryButton>
            <SecondaryButton type="button" onClick={selectThisPageOnly}>
              This page only
            </SecondaryButton>
            <PrimaryButton type="button" onClick={selectAllPages}>
              All pages
            </PrimaryButton>
          </div>
        </div>
      </ModalBasic>
    </div>
  );
};

export default IngestionDetail;
